


















































import Vue from 'vue'
// @ts-ignore
import { AgGridVue } from 'ag-grid-vue'
import { MasterDataActionsEnum } from '@/types/enums'
import PlanningGroupsSettingsDialog from '@/components/settings/PlanningGroupsSettingsDialog.vue'
import ColorCellRenderer from '@/components/cell-renderer/ColorCellRenderer.vue'
import BooleanCellRenderer from '@/components/cell-renderer/BooleanCellRenderer.vue'
import {
  COMPANY_FETCH_DEPARTMENT_PLANNING_GROUPS,
  COMPANY_UPDATE_DELETE_PLANNING_GROUP
} from '@/store/action-types'
import { CreatePlanningGroup } from '@/types/payload'
import { GridOptions } from '@/types'
import { PlanningGroupsSettings } from '@/model/settings/PlanningGroupsSettings'
import DeleteDialog from '@/components/DeleteDialog.vue'
import { hexToRgba } from '@/services/hex-to-rgba'

interface ComponentData {
  show: boolean
  gridData: Array<PlanningGroupsSettings>
  gridOptions: GridOptions
  action: MasterDataActionsEnum | undefined
  currentSelectedRowId: string
  currentSelectedPlanningGroup: PlanningGroupsSettings | undefined
  gridFrameworkComponents: Object
  loading: boolean
  showErrorSnackbar: boolean
  showSuccessSnackbar: boolean
  errorMessage: string
  successMessage: string
  showDeleteDialog: boolean
  overlayNoRowsTemplate: string
}

export default Vue.extend({
  name: 'PlanningGroupsPage',

  components: {
    AgGridVue,
    PlanningGroupsSettingsDialog,
    DeleteDialog
  },

  props: {
    departmentId: Number
  },

  data (): ComponentData {
    return {
      action: undefined,
      currentSelectedRowId: '',
      currentSelectedPlanningGroup: undefined,
      show: false,
      gridData: [],
      gridOptions: {
        columnDefs: [
          { headerName: this.$t('planningGroupsSettings.planningGroups'), field: 'planningGroupName', width: 300 },
          {
            headerName: this.$t('planningGroupsSettings.color'),
            field: 'color',
            cellRenderer: 'colorRenderer',
            width: 300
          }
        ]
      },
      gridFrameworkComponents: {
        colorRenderer: ColorCellRenderer,
        booleanRenderer: BooleanCellRenderer
      },
      loading: false,
      showErrorSnackbar: false,
      showSuccessSnackbar: false,
      errorMessage: '',
      successMessage: '',
      showDeleteDialog: false,
      overlayNoRowsTemplate: "<span>"+this.$t('errors.noRowsToShow')+"</span>"
    }
  },

  computed: {
    isAddEnabled (): boolean {
      return !!this.departmentId
    },

    isEditEnable (): boolean {
      return !!this.currentSelectedRowId
    },

    isDeleteEnable (): boolean {
      return !!this.currentSelectedRowId
    },

    toolbarColor (): string {
      return hexToRgba(this.$vuetify.theme.themes.light.primary)
    }
  },

  watch: {
    departmentId (newVal: number): void {
      if (newVal) {
        this.fetchPlanningGroupsList()
      }
    },

    planningGroupId (newVal: number): void {
      if (newVal) {
        this.currentSelectedRowId = ''
        this.currentSelectedPlanningGroup = undefined
        this.fetchPlanningGroupsList()
      }
    }
  },

  methods: {
    onAddButtonClick (): void {
      this.currentSelectedPlanningGroup = undefined
      this.show = true
      this.action = MasterDataActionsEnum.Create
      this.successMessage = this.$t('success.dataHaveBeenSaved').toString()
      this.errorMessage = this.$t('errors.dataCouldNotBeSaved').toString()
    },

    onEditButtonClick (): void {
      this.currentSelectedPlanningGroup = this.gridData.find((item: any) => item.id === parseInt(this.currentSelectedRowId))
      this.show = true
      this.action = MasterDataActionsEnum.Update
      this.successMessage = this.$t('success.dataHaveBeenSaved').toString()
      this.errorMessage = this.$t('errors.dataCouldNotBeSaved').toString()
    },

    onDeleteButtonClick (): void {
      this.currentSelectedPlanningGroup = this.gridData.find((item: any) => item.id === parseInt(this.currentSelectedRowId))
      this.showDeleteDialog = true
      this.action = MasterDataActionsEnum.Delete
      this.successMessage = this.$t('success.dataHaveBeenDeleted').toString()
      this.errorMessage = this.$t('errors.dataCouldNotBeDeleted').toString()
    },

    onDeleteDialogClose (): void {
      this.showDeleteDialog = false
      this.successMessage = this.$t('success.dataHaveBeenSaved').toString()
      this.errorMessage = this.$t('errors.dataCouldNotBeSaved').toString()
    },

    onDeleteDialogSave (): void {
      if (this.currentSelectedPlanningGroup && this.action === MasterDataActionsEnum.Delete) {
        const payload: CreatePlanningGroup = {
          action: this.action,
          departmentId: this.departmentId,
          planningGroupId: this.currentSelectedPlanningGroup.planningGroupId,
          color: this.currentSelectedPlanningGroup.color ? this.currentSelectedPlanningGroup.color.replace('#', '') : 'fff',
          id: this.currentSelectedPlanningGroup.id,
        }
        this.savePlanningGroup(payload)
      }
      this.showDeleteDialog = false
    },

    onRowClicked (params: any): void {
      this.currentSelectedRowId = params.node.data.id
    },

    onRowDoubleClicked (params: any) {
      this.currentSelectedRowId = params.node.data.id
      this.currentSelectedPlanningGroup = this.gridData.find((item: any) => item.id === parseInt(this.currentSelectedRowId))
      this.show = true
      this.action = MasterDataActionsEnum.Update
    },

    onSaveDialog (data: CreatePlanningGroup): void {
      if (this.action === MasterDataActionsEnum.Create) {
        const payload: CreatePlanningGroup = {
          action: this.action,
          departmentId: this.departmentId,
          planningGroupId: data.planningGroupId,
          color: data.color,
          id: null,
        }
        this.savePlanningGroup(payload)
      } else if (this.action === MasterDataActionsEnum.Update && this.currentSelectedPlanningGroup) {
        const payload: CreatePlanningGroup = {
          action: this.action,
          departmentId: this.currentSelectedPlanningGroup.departmentId,
          planningGroupId: this.currentSelectedPlanningGroup.planningGroupId,
          color: data.color,
          id: this.currentSelectedPlanningGroup.id,
        }
        this.savePlanningGroup(payload)
      }
    },

    async savePlanningGroup (data: CreatePlanningGroup): Promise<void> {
      try {
        this.loading = true
        await this.$store.dispatch(COMPANY_UPDATE_DELETE_PLANNING_GROUP, data)
        this.loading = false
        this.show = false
        this.showSuccessSnackbar = true
        this.currentSelectedRowId = ''
        this.currentSelectedPlanningGroup = undefined
        this.fetchPlanningGroupsList()
      } catch (e) {
        if(this.action === MasterDataActionsEnum.Delete && e === 'error' && this.currentSelectedPlanningGroup) {
          this.errorMessage = this.$t('planningGroupsSettings.thePlanningGroupCouldNotBeDeleted').toString().replace('%s', `"${this.currentSelectedPlanningGroup.planningGroupName}"`)
        }
        this.loading = false
        this.showErrorSnackbar = true
      }
    },

    onCloseDialog (): void {
      this.show = false
    },

    async fetchPlanningGroupsList (): Promise<void> {
      const data: Array<PlanningGroupsSettings> = await this.$store.dispatch(COMPANY_FETCH_DEPARTMENT_PLANNING_GROUPS, { departmentId: this.departmentId })
      this.gridData = data
    }
  },

  async mounted () {
    this.fetchPlanningGroupsList()
  }
})
